import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BrokerMapView, { type CityCluster } from './components/BrokerMapView';
import { fetchBrokerMap } from '../../services/brokerMapService';
import type { BrokerMapEntry } from '../../types/brokerMap';

type TypeFilter = 'all' | 'individual' | 'company' | 'developer';

const TYPE_META: Record<'individual' | 'company' | 'developer', { color: string; chip: string; en: string; ar: string }> = {
  individual: { color: '#2563eb', chip: 'bg-blue-50 text-blue-700 border-blue-200', en: 'Individual', ar: 'فرد' },
  company: { color: '#003930', chip: 'bg-purple-50 text-purple-700 border-purple-200', en: 'Company', ar: 'شركة' },
  developer: { color: '#f59e0b', chip: 'bg-amber-50 text-amber-700 border-amber-200', en: 'Developer', ar: 'مطوّر' },
};

interface CityGroup extends CityCluster { brokers: BrokerMapEntry[]; }

function brokerHref(b: BrokerMapEntry): string {
  if (b.type === 'developer') return `/developers/${b.slug}`;
  if (b.type === 'company') return `/companies/${b.slug}`;
  return `/broker/${b.slug}`;
}

function BrokerCardItem({ b, isRTL }: { b: BrokerMapEntry; isRTL: boolean }) {
  const meta = TYPE_META[b.type];
  return (
    <a
      href={brokerHref(b)}
      target="_blank"
      rel="noreferrer"
      className="group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#003930]/50 hover:shadow-md"
    >
      {b.avatarUrl ? (
        <img src={b.avatarUrl} alt={b.displayName} className="h-11 w-11 shrink-0 rounded-xl object-cover ring-1 ring-slate-200" />
      ) : (
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-lg font-bold text-white" style={{ background: meta.color }}>
          {b.displayName.charAt(0).toUpperCase()}
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          <span className="truncate text-sm font-semibold text-slate-800">{b.displayName}</span>
          {b.verified && <i className="ri-verified-badge-fill text-[15px] text-[#003930]" title={isRTL ? 'موثّق' : 'Verified'} />}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-slate-500">
          <span className={`rounded-full border px-1.5 py-0.5 font-medium ${meta.chip}`}>{isRTL ? meta.ar : meta.en}</span>
          <span className="inline-flex items-center gap-0.5"><i className="ri-home-4-line" />{b.totals.listings}</span>
          {b.avgRating > 0 && <span className="inline-flex items-center gap-0.5"><i className="ri-star-fill text-amber-400" />{b.avgRating.toFixed(1)}</span>}
        </div>
      </div>
      <i className={`ri-arrow-${isRTL ? 'left' : 'right'}-s-line text-lg text-slate-300 transition-colors group-hover:text-[#003930]`} />
    </a>
  );
}

export default function BrokersMapPage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const [allBrokers, setAllBrokers] = useState<BrokerMapEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedCity, setSelectedCity] = useState<string | null>(null);

  // Fetch ALL brokers ONCE. Every filter below is applied client-side, so
  // switching filters is instant (no per-filter server round-trip → no 8s wait).
  useEffect(() => {
    setLoading(true);
    fetchBrokerMap({})
      .then(setAllBrokers)
      .catch(() => setAllBrokers([]))
      .finally(() => setLoading(false));
  }, []);

  const visible = useMemo(() => {
    const s = search.trim().toLowerCase();
    return allBrokers.filter((b) =>
      (typeFilter === 'all' || b.type === typeFilter)
      && (!verifiedOnly || b.verified)
      && (!s || b.displayName.toLowerCase().includes(s)),
    );
  }, [allBrokers, typeFilter, verifiedOnly, search]);

  // Group brokers by city (a broker that lists in N cities appears in N groups).
  const cityGroups = useMemo<CityGroup[]>(() => {
    const map = new Map<string, CityGroup>();
    for (const b of visible) {
      for (const c of b.cities) {
        if (c.lat == null || c.lng == null) continue;
        const key = c.cityId || `${c.lat},${c.lng}`;
        let g = map.get(key);
        if (!g) { g = { cityId: key, name: c.name, lat: c.lat, lng: c.lng, count: 0, totalListings: 0, brokers: [] }; map.set(key, g); }
        g.brokers.push(b);
        g.count += 1;
        g.totalListings += c.listingCount;
      }
    }
    return [...map.values()].sort((a, b) => b.count - a.count);
  }, [visible]);

  const clusters = useMemo<CityCluster[]>(
    () => cityGroups.map(({ cityId, name, lat, lng, count, totalListings }) => ({ cityId, name, lat, lng, count, totalListings })),
    [cityGroups],
  );

  const selectedGroup = useMemo(() => cityGroups.find((g) => g.cityId === selectedCity) || null, [cityGroups, selectedCity]);

  // Side-panel list: the selected city's brokers, or the top brokers overall.
  const panelBrokers = useMemo(() => {
    const list = selectedGroup ? selectedGroup.brokers : visible;
    return [...list].sort((a, b) => b.totals.score - a.totals.score).slice(0, selectedGroup ? 200 : 40);
  }, [selectedGroup, visible]);

  const typeColor = typeFilter === 'all' ? '#055A4C' : TYPE_META[typeFilter].color;

  return (
    <div className="flex h-[calc(100vh-64px)] w-full" dir={i18n.dir()}>
      <aside className={`flex w-80 shrink-0 flex-col border-gray-200 bg-slate-50/60 ${isRTL ? 'border-l' : 'border-r'}`}>
        <div className="border-b border-slate-200 bg-white p-4">
          <h1 className="mb-1 text-lg font-bold text-[var(--pv-color-primary)]">
            {t('brokerMap.title', { defaultValue: 'Brokers across the Kingdom' })}
          </h1>
          <p className="mb-3 text-xs text-gray-500">
            {t('brokerMap.subtitle', { defaultValue: 'Explore brokers and companies by where they operate. Tap a city to see who lists there.' })}
          </p>

          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('brokerMap.searchPlaceholder', { defaultValue: 'Search brokers…' })}
            className="mb-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-[#003930] focus:outline-none focus:ring-1 focus:ring-[#003930]"
          />

          <div className="mb-3 flex flex-wrap gap-2 text-xs">
            {(['all', 'individual', 'company', 'developer'] as const).map((tf) => (
              <button
                key={tf}
                onClick={() => { setTypeFilter(tf); setSelectedCity(null); }}
                className={`rounded-full px-3 py-1 font-medium transition-colors ${typeFilter === tf
                  ? tf === 'developer' ? 'bg-amber-500 text-white'
                    : tf === 'company' ? 'bg-purple-600 text-white'
                      : tf === 'individual' ? 'bg-blue-600 text-white'
                        : 'bg-indigo-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
              >
                {t(`brokerMap.type.${tf}`, {
                  defaultValue: tf === 'all' ? 'All' : tf === 'individual' ? 'Individuals'
                    : tf === 'company' ? 'Companies' : 'Developers',
                })}
              </button>
            ))}
          </div>

          <label className="flex items-center gap-2 text-xs text-gray-700">
            <input type="checkbox" checked={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.checked)} className="accent-[#003930]" />
            {t('brokerMap.verifiedOnly', { defaultValue: 'Verified only' })}
          </label>
        </div>

        {/* Header row: selected city (with back) or "top brokers" */}
        <div className="flex items-center justify-between px-4 pt-3 pb-2">
          {selectedGroup ? (
            <>
              <div className="min-w-0">
                <button onClick={() => setSelectedCity(null)} className="mb-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-[#003930] hover:underline">
                  <i className={`ri-arrow-${isRTL ? 'right' : 'left'}-line`} />{t('brokerMap.back', { defaultValue: 'All cities' })}
                </button>
                <h2 className="truncate text-sm font-bold text-[var(--pv-color-primary)]">
                  {selectedGroup.name || t('brokerMap.thisCity', { defaultValue: 'This city' })}
                </h2>
              </div>
              <span className="shrink-0 rounded-full bg-slate-200 px-2 py-0.5 text-[11px] font-semibold text-slate-600">
                {selectedGroup.brokers.length}
              </span>
            </>
          ) : (
            <h2 className="text-xs font-semibold uppercase tracking-wide text-gray-400">
              {t('brokerMap.topBrokers', { defaultValue: 'Top brokers' })}
            </h2>
          )}
        </div>

        {/* Cards */}
        <div className="flex-1 space-y-2 overflow-y-auto px-4 pb-4">
          {loading ? (
            <p className="py-8 text-center text-sm text-gray-400">{t('common.loading', { defaultValue: 'Loading…' })}</p>
          ) : panelBrokers.length === 0 ? (
            <p className="py-8 text-center text-sm text-gray-400">{t('brokerMap.empty', { defaultValue: 'No brokers match.' })}</p>
          ) : (
            panelBrokers.map((b) => <BrokerCardItem key={`${b.id}-${selectedCity ?? 'top'}`} b={b} isRTL={isRTL} />)
          )}
        </div>
      </aside>

      <div className="relative flex-1">
        {/* Legend */}
        <div className={`absolute z-10 m-3 flex flex-wrap gap-3 rounded-lg bg-white/90 px-3 py-2 text-[11px] text-gray-600 shadow backdrop-blur ${isRTL ? 'end-0' : 'start-0'}`}>
          <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full bg-blue-600" />{t('brokerMap.type.individual', { defaultValue: 'Individuals' })}</span>
          <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full bg-purple-600" />{t('brokerMap.type.company', { defaultValue: 'Companies' })}</span>
          <span className="flex items-center gap-1"><span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-500" />{t('brokerMap.type.developer', { defaultValue: 'Developers' })}</span>
        </div>
        <BrokerMapView
          clusters={clusters}
          color={typeColor}
          selectedCity={selectedCity}
          onSelectCity={setSelectedCity}
        />
      </div>
    </div>
  );
}
